<template>
    <div>
        <section class="properties">
            <div class="property">
                <span class="label">Serial ID:</span>
                <copyable-text :text="serialObject['serial_id']"></copyable-text>
            </div>
            <div class="property" v-if="serialObject['project_id'] !== activeProjectId">
                <span class="label">Project:</span> <span>{{projectIdToName[serialObject['project_id']]}}</span>
            </div>
            <div class="property">
                <span class="label">Modified:</span> <span>{{describeUpdated}}</span>
            </div>
            <div class="property">
                <span class="label">Created:</span> <span>{{describeCreated}}</span>
            </div>
        </section>
        <section class="documents" v-if="serialObject['documents'].length">
            <header class="section-header label">Bound Documents:</header>
            <div class="document-list">
                <document-list-item class="document-item" v-for="(item,index) in serialObject['documents']"
                                    :key="index" :item="item"
                                    @click.native="openDocument($event, item, serialObject)"
                                    :show-unbind-button="item['global_document_id']===parentDocumentObject['global_document_id']"
                                    @unbindSerial="unbindSerial"
                >
                    <template v-slot:padding
                              v-if="item['global_document_id']===parentDocumentObject['global_document_id']">
                        <span class="padding">- Current Document</span>
                    </template>
                </document-list-item>
            </div>
        </section>
        <div class="empty-indicator mb-2" v-else>
            <i>No Documents Bound</i>
        </div>
        <template v-if="inEditWorkMode">
            <section class="d-flex" v-if="showBindToSerialButton.canBind">
                <button @click="bindSerial" class="edit-involved-button">Bind to Serial</button>
            </section>
            <section class="warning py-2" v-if="showBindToSerialButton.reason">
                <i class="gf gf-info mr-1"></i> <span>Cannot bind because {{showBindToSerialButton.reason}}</span>
            </section>
        </template>
    </div>
</template>

<script>
    import DocumentListItem from "@/components/DocumentListItem";
    import {
        checkCrossPlatformEventCtrlKey,
        describeUsername,
        describeUnixTs,
        userSettingsManager,
        workModeEdit
    } from "@/utils";
    import {projectIdToName} from "@/config";
    import CopyableText from "@/components/CopyableText";

    export default {
        name: "SerialDescriber",
        components: {CopyableText, DocumentListItem},
        data() {
            return {
                projectIdToName
            }
        },
        props: {
            serialObject: Object,
            parentDocumentObject: Object,
        },
        methods: {
            checkCrossPlatformEventCtrlKey: checkCrossPlatformEventCtrlKey,
            unbindSerial() {
                this.eventHub.$emit('attemptToUnbindSerialFor' + this.parentDocumentObject['global_document_id'], this.serialObject['serial_id'])
            },
            bindSerial() {
                this.eventHub.$emit('attemptToBindSerialFor' + this.parentDocumentObject['global_document_id'], this.serialObject['serial_id'])
            },
            openDocument($event, item, serialObject,) {
                this.eventHub.$emit('openDocument', {
                    globalDocumentId: item['global_document_id'],
                    silent: checkCrossPlatformEventCtrlKey($event),
                    focusSerialId: serialObject['serial_id'],
                    projectId: this.parentDocumentObject['project_id'],
                })
            }
        },
        computed: {
            showBindToSerialButton() {
                const serialObject = this.serialObject;
                const parentDocumentObject = this.parentDocumentObject;

                const documents = serialObject['documents'];

                if (documents.map(item => item['global_document_id']).indexOf(parentDocumentObject['global_document_id']) !== -1) {
                    return {
                        canBind: false,
                    };
                }

                if (serialObject['project_id'] !== parentDocumentObject['project_id']) {
                    return {
                        canBind: false,
                        reason: 'the serial was created for a different project'
                    };
                }

                if (documents.length && documents[0]['custom_data']['stock_code'] !== parentDocumentObject['custom_data']['stock_code']) {
                    return {
                        canBind: false,
                        reason: 'there exists document bound to the serial with a different stock code'
                    }
                }

                return {
                    canBind: true
                };
            },
            describeCreated() {
                const serialObject = this.serialObject;
                let createTime = serialObject['create_time'];
                let createdBy = serialObject['created_by'];

                if (createTime) {
                    return `${describeUnixTs(createTime)} by ${describeUsername(createdBy, 'Unknown')}`;
                } else {
                    return 'Unknown';
                }
            },
            describeUpdated() {
                const serialObject = this.serialObject;
                let lastUpdate = serialObject['last_update'];
                let lastUpdateAdmin = serialObject['last_update_admin'];

                if (lastUpdate) {
                    return `${describeUnixTs(lastUpdate)} by ${describeUsername(lastUpdateAdmin, 'Unknown')}`;
                } else {
                    return 'Never';
                }
            },
            workMode() {
                return userSettingsManager.getWorkMode();
            },
            inEditWorkMode() {
                return this.workMode === workModeEdit;
            },
            activeProjectId() {
                return this.$route.params.pid;
            }
        }
    }
</script>

<style scoped lang="scss">
    @import "../assets/variables";

    .documents {
        margin: 0 0 0.5rem;

        .section-header {
            margin-bottom: 0.25rem;
        }

        .document-item {
            border: 1px solid lightgray;
            border-radius: 4px;
            margin-bottom: 0.5rem;
            background-color: #fafafa;
            cursor: pointer;

            &:hover {
                border: 1px solid rgba($primary-purple, 0.5);
                background-color: white;
            }

            .padding {
                margin: 0 0.5rem 0 0.25rem;
                color: gray;
            }
        }
    }

    .property {
        margin-bottom: 0.75rem;
        display: flex;
        align-items: center;

        .label {
            margin-right: 0.15rem;
        }
    }

    .label {
        color: gray;
        font-weight: 500;
    }

    .warning {
        color: $primary-orange;
        padding-left: 20px;
        position: relative;
        background-color: rgba($primary-orange, 0.12);
        padding-right: 0.25rem;
        font-weight: 500;

        .gf-info {
            font-size: 16px;
            position: absolute;
            top: 0;
            left: 3px;
            margin-top: 0.7rem;
        }

        span {
            line-height: 16px;
        }
    }

    .empty-indicator {
        color: gray;
    }
</style>
